/**
 * 顧客関連のAPIエンドポイント
 */

import apiClient from '../client';
import type { Customer, CustomerCreate, CustomerUpdate } from '../../../types';
import type { BalanceResponse } from '../../../types/api';
import type { RefundCreateRequest } from './transaction';
import type { TransactionDetail } from '../../../types/api';

export const customerApi = {
  /**
   * 顧客一覧取得
   * @param storeId - 店舗ID（オプション・認証トークンから自動取得されるため使用されません）
   * @param search - 検索キーワード（オプション）
   */
  getCustomers: async (storeId?: number, search?: string): Promise<Customer[]> => {
    const params = new URLSearchParams();

    // storeIdは認証トークンから自動取得されるため、クエリパラメータとして送信しない
    if (search && search.trim() !== '') {
      params.append('search', search);
    }

    const queryString = params.toString();
    return apiClient.get<Customer[]>(`/customers${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * 顧客一覧取得（残高込み）
   * @param storeId - 店舗ID（オプション・認証トークンから自動取得されるため使用されません）
   * @param search - 検索キーワード（オプション）
   */
  getCustomersWithBalances: async (storeId?: number, search?: string): Promise<Customer[]> => {
    const params = new URLSearchParams();

    // storeIdは認証トークンから自動取得されるため、クエリパラメータとして送信しない
    if (search && search.trim() !== '') {
      params.append('search', search);
    }

    const queryString = params.toString();
    const response = await apiClient.get<any[]>(
      `/customers/with-balances${queryString ? `?${queryString}` : ''}`
    );

    // Convert the response to Customer[] format (balance -> currentBalance)
    return response.map((customer) => ({
      ...customer,
      currentBalance: customer.balance,
      email: customer.email ?? null,
      address: customer.address ?? null,
      birthDate: customer.birth_date ?? null,
      notes: customer.notes ?? null,
      createdAt: customer.created_at || '',
      updatedAt: customer.updated_at || ''
    }));
  },

  /**
   * 顧客作成
   */
  createCustomer: async (customerData: CustomerCreate): Promise<Customer> => {
    return apiClient.post<Customer>('/customers', customerData);
  },

  /**
   * 顧客更新
   */
  updateCustomer: async (customerId: number, customerData: CustomerUpdate): Promise<Customer> => {
    return apiClient.put<Customer>(`/customers/${customerId}`, customerData);
  },

  /**
   * 顧客削除
   */
  deleteCustomer: async (customerId: number): Promise<void> => {
    return apiClient.delete<void>(`/customers/${customerId}`);
  },

  /**
   * 顧客残高取得
   */
  getCustomerBalance: async (customerId: number, storeId: number): Promise<BalanceResponse> => {
    // storeIdは認証トークンから自動取得されるため、クエリパラメータとして送信しない
    return apiClient.get<BalanceResponse>(`/customers/${customerId}/balance`);
  },

  /**
   * 顧客への返金
   */
  createRefund: async (
    customerId: number,
    request: RefundCreateRequest
  ): Promise<TransactionDetail> => {
    return apiClient.post<TransactionDetail>(`/customers/${customerId}/refund`, request);
  },

  /**
   * 顧客名重複チェック
   * @param storeId - 店舗ID（オプション・認証トークンから自動取得されるため使用されません）
   * @param name - 顧客名
   * @param excludeId - 除外する顧客ID（編集時用）
   */
  checkDuplicateName: async (
    storeId: number,
    name: string,
    excludeId?: number
  ): Promise<Customer[]> => {
    const params = new URLSearchParams({
      name: name
    });

    // storeIdは認証トークンから自動取得されるため、クエリパラメータとして送信しない
    if (excludeId !== undefined) {
      params.append('exclude_id', excludeId.toString());
    }

    return apiClient.get<Customer[]>(`/customers/check-duplicate?${params}`);
  }
};
